import { sql } from "@ts-safeql/sql-tag";
import * as pgx from "@visioni/a3s-pgx";
import { TenantId } from "@visioni/a3s-schema";
import { UserId } from "@visioni/a3s-users";
import { UseServiceOf } from "@visioni/a3s-services/factories";
import {
  NotificationCategory,
  NotificationChannelPreferences,
  NotificationPreference,
  NotificationPreferenceScope,
} from "./types";

function exactlyOne<T>(rows: T[]): T {
  if (rows.length !== 1) {
    throw new Error(`Unexpected result for query. Expected one row, received ${String(rows.length)}`);
  }
  const [only] = rows as [T];
  return only;
}

// #region Types

/**
 * Manages self-service notification preferences for account holders.
 *
 * Unlike the legacy per-camera contacts, every preference here belongs to a real
 * a3s/Auth0 user — there is no arbitrary/unauthenticated recipient concept.
 *
 * Deliberately does not check the user's camera/site access anywhere in this
 * service — a user can save an "All Cameras" preference regardless of what they
 * currently have access to. Enforcing access here would go stale the moment
 * permissions change after the save; the only correct place to check is at
 * dispatch time, evaluated fresh against the user's *current* access when a
 * notification is about to actually go out (e.g. "All Cameras" means every
 * camera the user has access to *right now*, not a frozen set from when they
 * saved the preference).
 *
 * @feature alarmSchedules
 * @service notificationPreferences
 */
export type NotificationPreferencesService = {
  /**
   * Retrieves every notification preference a user has saved, grouped by scope:
   * their "All Cameras" row (if set), every site row, and every camera row.
   * Each scope's `NotificationCategoryPreferences` is assembled from up
   * to two underlying rows (one per {@link NotificationCategory}) — a category with
   * no saved row defaults to all channels disabled, same as an explicit all-false row.
   *
   * Does not combine scopes into what is effective for a given camera — that's
   * `resolveEffectiveRecipients` — since this returns the raw set a settings
   * screen needs to render, not a single resolved answer. Access control is a
   * separate concern again — see this type's own doc comment.
   */
  readonly getNotificationPreferences: (tenant: TenantId, userId: UserId) => Promise<NotificationPreference[]>;

  /**
   * Replaces a user's channel preferences for one scope (see
   * {@link NotificationPreferenceScope}) and one category (see
   * {@link NotificationCategory}), creating the row if it doesn't exist yet.
   * Overwrites all three channels with the values given — a full replace, not a
   * merge, since the caller always holds the complete channel state for that one
   * category (a settings screen's local state for that row), not a partial patch.
   *
   * Deletes the row instead of writing it when the given channels are all false —
   * an all-false row and no row at all already mean the same thing to every
   * reader (see `getNotificationPreferences`), so keeping it around is just
   * accumulating rows nobody reads. A no-op (returns all-false, writes nothing)
   * if no row existed for this scope+category to begin with.
   *
   * Intended to be called once per toggle, immediately, rather than batched behind
   * a "Save" button — matches the autosave convention of most toggle-based settings
   * screens (Gmail, Slack, iOS/Android system settings). Since each category is its
   * own row, a single toggle only ever writes (or deletes) the one row it belongs to.
   */
  readonly setNotificationPreferenceCategory: (
    tenant: TenantId,
    userId: UserId,
    scope: NotificationPreferenceScope,
    category: NotificationCategory,
    channels: NotificationChannelPreferences,
  ) => Promise<NotificationChannelPreferences>;

  /**
   * The dispatch-side counterpart to `getNotificationPreferences`: given one
   * specific camera and category, resolves every user *effectively* opted in
   * to it. Used when an event just happened on this camera and dispatch
   * needs the full recipient list, not one user's own settings view.
   *
   * Scopes combine by OR, per user and per channel (see
   * {@link NotificationPreferenceScope}): a channel is on if the camera's own
   * row, its site's row or the allCameras row has it on. A narrower row can
   * add channels but cannot remove channels a broader row grants — matching
   * what the settings screen shows as inherited.
   *
   * Returns every channel — `email`, `sms` and `push` — so each dispatcher
   * picks the one it sends. Does not check the resolved users' *current*
   * camera access, nor translate a legacy-shaped `userId` to its linked Auth0
   * identity — both are the caller's responsibility (see `a3s-access`'s
   * tenant access checker and legacy-id translation helper).
   */
  readonly resolveEffectiveRecipients: (
    tenant: TenantId,
    camera: Extract<NotificationPreferenceScope, { kind: "camera" }>,
    category: NotificationCategory,
  ) => Promise<{ userId: UserId; channels: NotificationChannelPreferences }[]>;
};

// #endregion

// #region Implementation

/** `notification_type` is `notification_preferences`' `notification_category` enum column, read back as text. */
type NotificationPreferenceRow = {
  tenant_id: string;
  user_id: string;
  site_id: string;
  asset_id: string;
  camera_id: string;
  notification_type: string;
  email: boolean;
  sms: boolean;
  push: boolean;
};

type NotificationChannelsRow = {
  email: boolean;
  sms: boolean;
  push: boolean;
};

const NO_CHANNELS: NotificationChannelPreferences = { email: false, sms: false, push: false };

/** All channels disabled for both categories — the default for a scope with no saved rows at all. */
const DEFAULT_CATEGORY_PREFERENCES = {
  alarmEvent: NO_CHANNELS,
  cameraConfigChange: NO_CHANNELS,
};

/** `notification_preferences.notification_type`'s enum values, keyed by {@link NotificationCategory}. */
const CATEGORY_TO_DB_TYPE: Record<NotificationCategory, string> = {
  alarmEvent: "alarm_event",
  cameraConfigChange: "camera_config_change",
};

const DB_TYPE_TO_CATEGORY: Record<string, NotificationCategory> = {
  alarm_event: "alarmEvent",
  camera_config_change: "cameraConfigChange",
};

function dbTypeToCategory(notificationType: string): NotificationCategory {
  const category = DB_TYPE_TO_CATEGORY[notificationType];
  if (category === undefined) {
    throw new Error(`Unknown notification_preferences.notification_type from database: ${notificationType}`);
  }
  return category;
}

/**
 * `site_id`/`asset_id`/`camera_id` are `''` (not NULL) sentinels for "unscoped" —
 * see `notification_preferences.sql`. This is the row → scope-discriminant mapping
 * the table's own comments describe.
 */
function columnsToScope(siteId: string, assetId: string, cameraId: string): NotificationPreferenceScope {
  if (cameraId !== "") {
    return { kind: "camera", siteId, assetId, cameraId };
  }
  if (siteId !== "") {
    return { kind: "site", siteId };
  }
  return { kind: "allCameras" };
}

/** The inverse of {@link columnsToScope} — a scope's `''`-sentinel column values. */
function scopeToColumns(scope: NotificationPreferenceScope): { siteId: string; assetId: string; cameraId: string } {
  switch (scope.kind) {
    case "allCameras":
      return { siteId: "", assetId: "", cameraId: "" };
    case "site":
      return { siteId: scope.siteId, assetId: "", cameraId: "" };
    case "camera":
      return { siteId: scope.siteId, assetId: scope.assetId, cameraId: scope.cameraId };
  }
}

const getNotificationPreferences = async (
  db: pgx.Database,
  tenant: TenantId,
  userId: UserId,
): Promise<NotificationPreference[]> => {
  const candidateUserIds = [UserId.toString(userId)];

  const result = await db.query<NotificationPreferenceRow>(sql`
    SELECT
      tenant_id,
      user_id,
      site_id,
      asset_id,
      camera_id,
      notification_type::TEXT,
      email,
      sms,
      push
    FROM
      notification_preferences
    WHERE
      tenant_id = ${TenantId.toString(tenant)}
      AND user_id = ANY (${candidateUserIds}::TEXT[])
  `);

  type ScopeMeta = { tenantId: string; siteId: string; assetId: string; cameraId: string };

  const scopeMeta = new Map<string, ScopeMeta>();
  // Keyed by `${scopeKey}:${category}`.
  const slots = new Map<string, { channels: NotificationChannelPreferences; isPrimary: boolean }>();
  const primaryUserIdString = UserId.toString(userId);

  for (const row of result.rows) {
    const scopeKey = JSON.stringify([row.site_id, row.asset_id, row.camera_id]);
    const category = dbTypeToCategory(row.notification_type);
    const slotKey = `${scopeKey}:${category}`;
    const isPrimary = row.user_id === primaryUserIdString;

    if (slots.get(slotKey)?.isPrimary && !isPrimary) continue;

    slots.set(slotKey, { channels: { email: row.email, sms: row.sms, push: row.push }, isPrimary });
    if (!scopeMeta.has(scopeKey)) {
      scopeMeta.set(scopeKey, {
        tenantId: row.tenant_id,
        siteId: row.site_id,
        assetId: row.asset_id,
        cameraId: row.camera_id,
      });
    }
  }

  return Array.from(scopeMeta.entries()).map(([scopeKey, meta]) => ({
    tenantId: meta.tenantId,
    userId,
    scope: columnsToScope(meta.siteId, meta.assetId, meta.cameraId),
    preferences: {
      alarmEvent: slots.get(`${scopeKey}:alarmEvent`)?.channels ?? DEFAULT_CATEGORY_PREFERENCES.alarmEvent,
      cameraConfigChange:
        slots.get(`${scopeKey}:cameraConfigChange`)?.channels ?? DEFAULT_CATEGORY_PREFERENCES.cameraConfigChange,
    },
  }));
};

const setNotificationPreferenceCategory = async (
  db: pgx.Database,
  tenant: TenantId,
  userId: UserId,
  scope: NotificationPreferenceScope,
  category: NotificationCategory,
  channels: NotificationChannelPreferences,
): Promise<NotificationChannelPreferences> => {
  // Re-validated at runtime rather than trusted from the TS type: TypeScript's
  // structural typing doesn't enforce NotificationPreferenceScope's non-empty-id
  // refinement, so an unvalidated caller (e.g. a naive cast of decoded JSON) could
  // otherwise slip a `site`/`camera` scope with an empty id through — which would
  // write the same all-empty sentinel row as `allCameras` and silently collide.
  const { siteId, assetId, cameraId } = scopeToColumns(NotificationPreferenceScope.parse(scope));
  const notificationType = CATEGORY_TO_DB_TYPE[NotificationCategory.parse(category)];

  if (!channels.email && !channels.sms && !channels.push) {
    await db.query(sql`
      DELETE FROM notification_preferences
      WHERE
        tenant_id = ${TenantId.toString(tenant)}
        AND user_id = ${UserId.toString(userId)}
        AND site_id = ${siteId}
        AND asset_id = ${assetId}
        AND camera_id = ${cameraId}
        AND notification_type = ${notificationType}::notification_category
    `);
    return channels;
  }

  const result = await db.query<NotificationChannelsRow>(sql`
    INSERT INTO
      notification_preferences (
        tenant_id,
        user_id,
        site_id,
        asset_id,
        camera_id,
        notification_type,
        email,
        sms,
        push
      )
    VALUES
      (
        ${TenantId.toString(tenant)},
        ${UserId.toString(userId)},
        ${siteId},
        ${assetId},
        ${cameraId},
        ${notificationType}::notification_category,
        ${channels.email},
        ${channels.sms},
        ${channels.push}
      )
    ON CONFLICT (
      tenant_id,
      user_id,
      site_id,
      asset_id,
      camera_id,
      notification_type
    ) DO UPDATE
    SET
      email = EXCLUDED.email,
      sms = EXCLUDED.sms,
      push = EXCLUDED.push,
      updated_at = now()
    RETURNING
      email,
      sms,
      push
  `);

  return exactlyOne(result.rows);
};

const resolveEffectiveRecipients = async (
  db: pgx.Database,
  tenant: TenantId,
  camera: Extract<NotificationPreferenceScope, { kind: "camera" }>,
  category: NotificationCategory,
): Promise<{ userId: UserId; channels: NotificationChannelPreferences }[]> => {
  const notificationType = CATEGORY_TO_DB_TYPE[NotificationCategory.parse(category)];

  // One row per user: each channel OR'd across every scope row that matches
  // this camera. The COALESCEs never fire (every group has at least one row
  // and the columns are NOT NULL); they only let the query type say so.
  const result = await db.query<{ user_id: string; email: boolean; sms: boolean; push: boolean }>(sql`
    SELECT
      user_id,
      COALESCE(bool_or(email), FALSE) AS email,
      COALESCE(bool_or(sms), FALSE) AS sms,
      COALESCE(bool_or(push), FALSE) AS push
    FROM
      notification_preferences
    WHERE
      tenant_id = ${TenantId.toString(tenant)}
      AND notification_type = ${notificationType}::notification_category
      AND (
        (
          site_id = ${camera.siteId}
          AND asset_id = ${camera.assetId}
          AND camera_id = ${camera.cameraId}
        )
        OR (
          site_id = ${camera.siteId}
          AND asset_id = ''
          AND camera_id = ''
        )
        OR (
          site_id = ''
          AND asset_id = ''
          AND camera_id = ''
        )
      )
    GROUP BY
      user_id
  `);

  // Defensive, not load-bearing: `setNotificationPreferenceCategory` deletes a
  // row the instant every channel is false, so no stored row is all-false and
  // neither is any OR over them. Guards against that invariant ever drifting.
  return result.rows
    .filter((row) => row.email || row.sms || row.push)
    .map((row) => ({
      userId: UserId.fromString(row.user_id),
      channels: { email: row.email, sms: row.sms, push: row.push },
    }));
};

// #endregion

/** Creates a NotificationPreferencesService factory bound to infrastructure dependencies. */
export const makeNotificationPreferencesService =
  (db: pgx.Database): UseServiceOf<NotificationPreferencesService> =>
  (_ctx) => ({
    getNotificationPreferences: (tenant, userId) => getNotificationPreferences(db, tenant, userId),
    setNotificationPreferenceCategory: (tenant, userId, scope, category, channels) =>
      setNotificationPreferenceCategory(db, tenant, userId, scope, category, channels),
    resolveEffectiveRecipients: (tenant, camera, category) => resolveEffectiveRecipients(db, tenant, camera, category),
  });
